import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Input,
  Textarea,
  Tooltip,
  Typography,
  IconButton,
} from '@mui/joy';
import DeleteIcon from '@mui/icons-material/Delete';
import { formatTimeAgo } from '../../utils/dateUtils';
import type {
  CongregationBatchPostReply,
  CongregationMember,
} from '../../types/congregation';
import {
  listPostReplies,
  createPostReply,
  deletePostReply,
} from '../../api';

interface RepliesSectionProps {
  postId: number;
  currentMember: CongregationMember | null;
  canReply?: boolean;
  readOnly?: boolean;
  canDeleteAnyReply?: boolean;
  onError?: (error: string) => void;
  onSuccess?: (message: string) => void;
  onDeleteReply?: (replyId: number) => Promise<void>;
}

interface ExpandedThreads {
  [key: number]: boolean;
}

export default function RepliesSection({
  postId,
  currentMember,
  canReply = false,
  readOnly = true,
  canDeleteAnyReply = false,
  onError,
  onSuccess,
  onDeleteReply,
}: RepliesSectionProps) {
  const [replies, setReplies] = React.useState<CongregationBatchPostReply[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [replying, setReplying] = React.useState(false);
  const [replyContent, setReplyContent] = React.useState('');
  const [expandedThreads, setExpandedThreads] = React.useState<ExpandedThreads>({});
  const loadedRef = React.useRef(false);

  React.useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    const loadReplies = async () => {
      try {
        setLoading(true);
        const data = await listPostReplies(postId);
        setReplies(data || []);
      } catch (error) {
        console.error('Failed to load replies:', error);
        onError?.('Failed to load replies');
      } finally {
        setLoading(false);
      }
    };

    loadReplies();
  }, [postId, onError]);

  const handleReplySubmit = async (e: React.FormEvent, parentReplyId?: number) => {
    e.preventDefault();
    if (!replyContent.trim() || !currentMember) return;

    try {
      setReplying(true);
      const newReply = await createPostReply(postId, {
        content: replyContent,
        parentReplyId,
      });

      if (newReply) {
        setReplies((prev) => [...prev, newReply]);
        setReplyContent('');
        onSuccess?.('Reply posted');
      }
    } catch (error) {
      console.error('Failed to post reply:', error);
      onError?.('Failed to post reply');
    } finally {
      setReplying(false);
    }
  };

  const handleDeleteReply = async (replyId: number) => {
    try {
      if (onDeleteReply) {
        await onDeleteReply(replyId);
      } else {
        await deletePostReply(replyId);
      }
      setReplies((prev) => prev.filter((r) => r.id !== replyId));
      onSuccess?.('Reply deleted');
    } catch (error) {
      console.error('Failed to delete reply:', error);
      onError?.('Failed to delete reply');
    }
  };

  const topLevelReplies = replies.filter((r) => !r.parentReplyId);
  const getRepliesByParent = (parentId: number): CongregationBatchPostReply[] => {
    return replies.filter((r) => r.parentReplyId === parentId);
  };

  const RenderReply = ({
    reply,
    level = 0,
  }: {
    reply: CongregationBatchPostReply;
    level?: number;
  }) => {
    const childReplies = getRepliesByParent(reply.id);
    const isExpanded = expandedThreads[reply.id] ?? childReplies.length === 0;

    return (
      <Box key={reply.id} ml={level * 2}>
        <Card sx={{ mb: 1 }}>
          <CardContent sx={{ py: 1 }}>
            {/* Reply Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1} spacing={1}>
              <Stack direction="row" spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
                <Avatar 
                  size="sm" 
                  src={reply.authorPhotoUrl || undefined}
                  sx={{ bgcolor: 'primary.softBg', color: 'primary.solidColor' }}
                >
                  {(reply.authorName?.charAt(0) || '?').toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography level="title-sm" fontWeight="bold" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {reply.authorName}
                  </Typography>
                  <Typography level="body-xs" color="neutral">
                    {formatTimeAgo(new Date(reply.createdAt))}
                  </Typography>
                </Box>
              </Stack>

              {!readOnly && (currentMember?.id === reply.authorMemberId || canDeleteAnyReply) && (
                <Tooltip title={currentMember?.id === reply.authorMemberId ? "Delete your reply" : "Delete reply (Moderator)"}>
                  <IconButton
                    size="sm"
                    color="danger"
                    onClick={() => handleDeleteReply(reply.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>

            {/* Reply Content */}
            <Typography level="body-sm" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
              {reply.content}
            </Typography>

            {/* Child Replies */}
            {childReplies.length > 0 && (
              <Box mt={1}>
                <Button
                  size="sm"
                  onClick={() =>
                    setExpandedThreads((prev) => ({
                      ...prev,
                      [reply.id]: !isExpanded,
                    }))
                  }
                >
                  {isExpanded ? '−' : '+'} {childReplies.length} repl
                  {childReplies.length === 1 ? 'y' : 'ies'}
                </Button>

                {isExpanded && (
                  <Box mt={1}>
                    {childReplies.map((childReply) => (
                      <RenderReply
                        key={childReply.id}
                        reply={childReply}
                        level={level + 1}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  if (replies.length === 0) {
    return (
      <Box>
        {canReply && currentMember && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <form onSubmit={(e) => handleReplySubmit(e)}>
                <Stack spacing={2}>
                  <Typography level="title-sm">No replies yet</Typography>
                  <Textarea
                    minRows={3}
                    placeholder="Add your reply..."
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    disabled={replying}
                    size="sm"
                  />
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      variant="solid"
                      type="submit"
                      disabled={!replyContent.trim() || replying}
                    >
                      {replying ? 'Posting...' : 'Reply'}
                    </Button>
                  </Stack>
                </Stack>
              </form>
            </CardContent>
          </Card>
        )}
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {topLevelReplies.map((reply) => (
        <RenderReply key={reply.id} reply={reply} />
      ))}

      {canReply && currentMember && (
        <Card>
          <CardContent>
            <form onSubmit={(e) => handleReplySubmit(e)}>
              <Stack spacing={2}>
                <Textarea
                  minRows={3}
                  placeholder="Add your reply..."
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  disabled={replying}
                  size="sm"
                />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    variant="solid"
                    type="submit"
                    disabled={!replyContent.trim() || replying}
                  >
                    {replying ? 'Posting...' : 'Reply'}
                  </Button>
                </Stack>
              </Stack>
            </form>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}
